import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

//========== Import components from MUI Library ============
//All Wrapper Elements
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";

//All Loading related components
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
//All CSS
import { makeStyles } from "tss-react/mui";
//All Element/ Controls
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import FormHelperText from "@mui/material/FormHelperText";

// All Common Element/ Controls
import { ButtonUI } from "../../Common";
import CommonLayout from "../../Common/Layout";

import HelpContextIcon from "../../Common/HelpPanel/HelpContextIcon";
import App from "../../../service/appService";
import { showToast } from "../../../store/actions";
import { navAppHosting } from "../../kmk/AppData";
import { computeOptions, deploymentTimeOutOptions, defaultTimeOutValue } from "./data";
import SmallToolbar from "../../Common/SmallToolbar";

const useStyles = makeStyles()({
  Header: {
    fontSize: "1.1rem",
    minHeight: "60px !important",
    backgroundColor: "#e8edf1",
    color: "black",
    marginTop: 0,
    borderRadius: 0,
    boxShadow: 0,
    fontWeight: 600,
  },
  HeaderTitle: {
    fontSize: 20,
    lineHeight: 1.5,
    paddingTop: 0,
    paddingRight: "20px",
    display: "inline-block",
  },
  container: {
    padding: 0,
    backgroundColor: "#FFFFFF",
  },
  subContainer: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    width: "100%",
    paddingBottom: "80px",
  },
  PanelContainer: {
    padding: "10px 30px",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    // minHeight: "250px",
  },
  Toolbar: {
    fontSize: "1.1rem",
    backgroundColor: "white",
    color: "black",
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },
  Panel: {
    padding: "10px 25px",
    backgroundColor: "#FFFFFF",
    marginTop: 0,
    borderRadius: 10,
    boxShadow: "none",
  },
  PanelRow: {
    padding: "12px 0",
  },
});

const PanelContainer = ({ children, title }) => {
  const { classes } = useStyles();

  return (
    <Box className={classes.PanelContainer}>
      <SmallToolbar>{title}</SmallToolbar>
      <Box className={classes.Panel}>
        <Box maxWidth="xl">{children}</Box>
      </Box>
    </Box>
  );
};

export default function AddApplication() {
  const { Workspace_id, App_id } = useParams();
  const { appIdentityProviders, appImageConfig } = useSelector((state) => state.config);
  const { workspace } = useSelector((state) => state.workspace);
  const WorkspaceName = workspace.Name;
  const { classes } = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentURL = useMemo(() => window.location.origin + "/", []);

  const newApplication = useMemo(() => App_id === "add", [App_id]);

  const breadcrumbArr = useMemo(
    () => [
      { link: "/my-workspaces", displayName: "Workspaces", active: false },
      { link: `/my-workspaces/${Workspace_id}`, displayName: WorkspaceName, active: false },
      { link: `/my-workspaces/${Workspace_id}/app-hosting`, displayName: navAppHosting.name, active: false },
      { link: "#", displayName: `${newApplication ? "Add" : "Edit"} Application`, active: true },
    ],
    [newApplication, Workspace_id, WorkspaceName]
  );

  //App Config
  const [appRuntimes, setAppRuntimes] = useState([]);

  //Info
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [targetURL, setTargetURL] = useState("");
  const [type, setType] = useState("");
  const [repoURL, setRepoURL] = useState("");
  const [branch, setBranch] = useState("");
  const [filePath, setFilePath] = useState("");
  const [runtime, setRuntime] = useState("");
  const [compute, setCompute] = useState(computeOptions[0].text);
  const [appCPU, setAppCPU] = useState(computeOptions[0].CPU);
  const [appRAM, setAppRAM] = useState(computeOptions[0].RAM);

  //Authentication
  const [identityProviderList, setIdentityProviderList] = useState([]);
  const [identityProvider, setIdentityProvider] = useState("");
  const [issuer, setIssuer] = useState("");
  const [authorizationEndpoint, setAuthorizationEndpoint] = useState("");
  const [tokenEndpoint, setTokenEndpoint] = useState("");
  const [userInfoEndpoint, setUserInfoEndpoint] = useState("");
  const [clientID, setClientID] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [readOnly, setReadOnly] = useState(false);

  //Advanced Settings
  const [setupScript, setSetupScript] = useState("");
  const [envVariables, setEnvVariables] = useState("");
  const [deploymentTimeOut, setDeploymentTimeOut] = useState(defaultTimeOutValue);

  const computeCost = useMemo(() => {
    const cost = ((parseFloat(appCPU) * 0.04048 + parseFloat(appRAM) * 0.004445) * 730) / 1024;
    return isNaN(cost) ? 0 : Math.round(cost);
  }, [appCPU, appRAM]);

  const appTypes = useMemo(
    () => appImageConfig.map((_config) => ({ type: _config.type, icon: _config.icon })),
    [appImageConfig]
  );

  // Get App Runtime From App Config Based on Select Type
  useEffect(() => {
    const _runtimes = appImageConfig.filter((_config) => _config.type === type);

    setAppRuntimes(_runtimes[0]?.runtimes || []);
    setRuntime("");
  }, [type, appImageConfig]);

  useEffect(() => {
    let providers = [];

    if (Object.entries(appIdentityProviders).length) {
      providers.push({ key: "SystemDefault", text: "System Default", detail: appIdentityProviders });
    }

    providers.push({ key: "OIDC", text: "OIDC (OpenID Connect)" });

    setIdentityProviderList(providers);
    setIdentityProvider(providers.length ? providers[0].key : "");
    if (providers[0].key === "SystemDefault") {
      setIssuer(appIdentityProviders?.issuer || "");
      setAuthorizationEndpoint(appIdentityProviders?.authorizationEndpoint || "");
      setTokenEndpoint(appIdentityProviders?.tokenEndpoint || "");
      setUserInfoEndpoint(appIdentityProviders?.userInfoEndpoint || "");
      setClientID(appIdentityProviders?.clientID || "");
      setClientSecret(appIdentityProviders?.clientSecret || "");
      setReadOnly(true);
    }

    if (providers[0].key === "OIDC") {
      setIssuer("");
      setAuthorizationEndpoint("");
      setTokenEndpoint("");
      setUserInfoEndpoint("");
      setClientID("");
      setClientSecret("");
      setReadOnly(false);
    }
  }, [appIdentityProviders]);

  const handleIDPOnChange = (event) => {
    const idp = event.target.value;
    setIdentityProvider(idp);
    if (idp === "SystemDefault") {
      setIssuer(appIdentityProviders?.issuer || "");
      setAuthorizationEndpoint(appIdentityProviders?.authorizationEndpoint || "");
      setTokenEndpoint(appIdentityProviders?.tokenEndpoint || "");
      setUserInfoEndpoint(appIdentityProviders?.userInfoEndpoint || "");
      setClientID(appIdentityProviders?.clientID || "");
      setClientSecret(appIdentityProviders?.clientSecret || "");
      setReadOnly(true);
    }

    if (idp === "OIDC") {
      setIssuer("");
      setAuthorizationEndpoint("");
      setTokenEndpoint("");
      setUserInfoEndpoint("");
      setClientID("");
      setClientSecret("");
      setReadOnly(false);
    }
  };

  const navigateToList = useCallback(() => {
    navigate(`/my-workspaces/${Workspace_id}/app-hosting`);
  }, [navigate, Workspace_id]);

  //handle save
  const handleSave = async () => {
    setLoading(true);
    const app = new App();

    const data = {
      name,
      targetURL,
      type,
      repoURL,
      branch,
      filePath,
      identityProvider,
      issuer,
      authorizationEndpoint,
      tokenEndpoint,
      userInfoEndpoint,
      clientID,
      clientSecret,
      setupScript,
      envVariables,
      deploymentTimeOut,
      runtime,
      compute,
      appCPU,
      appRAM,
      description: "",
    };

    const response = newApplication
      ? await app.addApp(Workspace_id, data)
      : await app.updateApp(Workspace_id, App_id, data);

    setLoading(false);

    if (response.success) {
      dispatch(
        showToast({
          message: response.message,
          type: "success",
        })
      );

      navigateToList();
    }
  };

  const onComputeChange = (event) => {
    const selectedCompute = computeOptions.find((cp) => cp.text === event.target.value);

    setCompute(selectedCompute.text);
    setAppCPU(selectedCompute.CPU);
    setAppRAM(selectedCompute.RAM);
  };

  const isSaveDisabled = useMemo(
    () =>
      !name.trim() ||
      !targetURL.trim() ||
      !repoURL.trim() ||
      !filePath.trim() ||
      !identityProvider.trim() ||
      !issuer.trim() ||
      !authorizationEndpoint.trim() ||
      !tokenEndpoint.trim() ||
      !userInfoEndpoint.trim() ||
      !clientID.trim() ||
      !clientSecret.trim(),
    [
      name,
      targetURL,
      repoURL,
      filePath,
      identityProvider,
      issuer,
      authorizationEndpoint,
      tokenEndpoint,
      userInfoEndpoint,
      clientID,
      clientSecret,
    ]
  );

  useEffect(() => {
    if (newApplication) return;

    setLoading(true);
    const app = new App();
    app
      .getApp(Workspace_id, App_id)
      .then((response) => {
        if (!response.success) return navigateToList();

        const {
          name,
          targetURL,
          type,
          repoURL,
          branch,
          filePath,
          identityProvider,
          issuer,
          authorizationEndpoint,
          tokenEndpoint,
          userInfoEndpoint,
          clientID,
          clientSecret,
          setupScript,
          deploymentTimeOut,
          envVariables,
          runtime,
          compute,
          appCPU,
          appRAM,
        } = response.data;

        setName(name);
        setTargetURL(targetURL);
        setType(type);
        setRepoURL(repoURL);
        setBranch(branch);
        setFilePath(filePath);
        setIdentityProvider(identityProvider);

        if (identityProvider === "SystemDefault" && Object.keys(appIdentityProviders).length) {
          setIssuer(appIdentityProviders.issuer);
          setAuthorizationEndpoint(appIdentityProviders.authorizationEndpoint);
          setTokenEndpoint(appIdentityProviders.tokenEndpoint);
          setUserInfoEndpoint(appIdentityProviders.userInfoEndpoint);
          setClientID(appIdentityProviders.clientID);
          setClientSecret(appIdentityProviders.clientSecret);
        } else {
          setIssuer(issuer);
          setAuthorizationEndpoint(authorizationEndpoint);
          setTokenEndpoint(tokenEndpoint);
          setUserInfoEndpoint(userInfoEndpoint);
          setClientID(clientID);
          setClientSecret(clientSecret);
        }

        setSetupScript(setupScript);
        setEnvVariables(envVariables);
        setDeploymentTimeOut(deploymentTimeOut || defaultTimeOutValue);
        setRuntime(runtime);
        setCompute(compute);
        setAppCPU(appCPU);
        setAppRAM(appRAM);
      })
      .catch(navigateToList)
      .finally(() => setLoading(false));
  }, [newApplication, App_id, Workspace_id, navigateToList, appIdentityProviders]);

  return (
    <CommonLayout title="App Hosting" breadcrumbArr={breadcrumbArr} loading={loading}>
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          flexGrow: 1,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Backdrop sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="primary" />
        </Backdrop>
        <AppBar
          sx={{
            position: "relative",
            boxShadow: 0,
            backgroundColor: "#e8edf1",
          }}
        >
          <Toolbar className={classes.Header}>
            <Grid container spacing={5} direction="row" alignItems={"center"}>
              <Grid
                size={{
                  sm: 12,
                  xs: 12
                }}>
                <Box sx={{ display: "inline-flex", verticalAlign: "sub" }}>
                  <Typography className={classes.HeaderTitle} gutterBottom component="div">
                    {newApplication ? "Add" : "Edit"} Application
                  </Typography>
                </Box>
                <Box sx={{ display: "inline-flex", paddingTop: "10px" }}>
                  <ButtonUI variant="contained" color="success" onClick={handleSave} disabled={isSaveDisabled}>
                    Save
                  </ButtonUI>
                  <ButtonUI style={{ marginLeft: 15 }} variant="contained" color="secondary" onClick={navigateToList}>
                    Cancel
                  </ButtonUI>
                  <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                    <HelpContextIcon screen={`Add Application`} />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Toolbar>
        </AppBar>

        <Box className={classes.subContainer}>
          <PanelContainer title="Info">
            <Grid container className={classes.PanelRow} spacing={3}>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4
                }}>
                <TextField
                  required
                  variant="outlined"
                  inputProps={{ maxLength: 50 }}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                  label="Name"
                  autoFocus
                  disabled={!newApplication}
                />
              </Grid>
            </Grid>
            <Grid container className={classes.PanelRow} spacing={3}>
              <Grid size={8}>
                <TextField
                  required
                  variant="outlined"
                  inputProps={{ maxLength: 50 }}
                  value={targetURL}
                  onBlur={(e) => {
                    //Target url autofill
                    const newVal = e.target.value
                      .replaceAll(" ", "-")
                      .replace(/[^a-zA-Z0-9]*?/g, "")
                      .toLowerCase();
                    setTargetURL(/^[a-zA-Z]+[a-zA-Z0-9]+[\d]*\*?$/.test(newVal) ? newVal : "");
                  }}
                  onChange={(e) => setTargetURL(e.target.value.toLowerCase())}
                  fullWidth
                  label="Target URL"
                  helperText={`${currentURL}${targetURL || "<target_url>"}/`}
                />
              </Grid>
            </Grid>
            <Grid container className={classes.PanelRow} spacing={3}>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4
                }}>
                <FormControl variant="outlined" fullWidth required>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={type}
                    label="Type"
                    onChange={(e) => setType(e.target.value)}
                    MenuProps={{ sx: { height: "220px" } }}
                  >
                    <MenuItem value="">Select Type</MenuItem>
                    {appTypes.map((_appType, _key) => (
                      <MenuItem key={_key} value={_appType.type}>
                        <img
                          src={`/images/${_appType.icon}`}
                          alt={_appType.type}
                          style={{ width: "20px", marginRight: "10px" }}
                        />
                        {_appType.type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container className={classes.PanelRow} spacing={3}>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4
                }}>
                <FormControl variant="outlined" fullWidth required>
                  <InputLabel>Runtime</InputLabel>
                  <Select
                    value={runtime}
                    label="Runtime"
                    onChange={(e) => setRuntime(e.target.value)}
                    MenuProps={{ sx: { height: "220px" } }}
                  >
                    <MenuItem value="">Select Runtime</MenuItem>
                    {appRuntimes.map((option, key) => (
                      <MenuItem key={key} value={option.name}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4
                }}>
                <FormControl variant="outlined" fullWidth required>
                  <InputLabel>Compute</InputLabel>
                  <Select
                    value={compute}
                    label="Compute"
                    onChange={onComputeChange}
                    MenuProps={{ sx: { height: "220px" } }}
                  >
                    {computeOptions.map((option) => (
                      <MenuItem key={option.text} value={option.text}>
                        {option.text}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Estimated Cost = ${computeCost} USD/Month</FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container className={classes.PanelRow} spacing={3}>
              <Grid size={8}>
                <TextField
                  required
                  variant="outlined"
                  inputProps={{ maxLength: 2500 }}
                  value={repoURL}
                  onChange={(e) => setRepoURL(e.target.value)}
                  fullWidth
                  label="Repository URL"
                />
              </Grid>
            </Grid>
            <Grid container className={classes.PanelRow} spacing={3}>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4
                }}>
                <TextField
                  variant="outlined"
                  inputProps={{ maxLength: 50 }}
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                  fullWidth
                  label="Branch"
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4
                }}>
                <TextField
                  required
                  variant="outlined"
                  inputProps={{ maxLength: 50 }}
                  value={filePath}
                  onChange={(e) => setFilePath(e.target.value)}
                  fullWidth
                  label="Main file path"
                />
              </Grid>
            </Grid>
          </PanelContainer>

          <PanelContainer title="Authentication">
            <Grid container className={classes.PanelRow} spacing={3}>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4
                }}>
                <FormControl variant="outlined" fullWidth required>
                  <InputLabel>Identity provider</InputLabel>
                  <Select
                    value={identityProvider}
                    label="Identity provider"
                    onChange={handleIDPOnChange}
                    MenuProps={{ sx: { height: "220px" } }}
                  >
                    {identityProviderList.map((option) => (
                      <MenuItem key={option.key} value={option.key}>
                        {option.text}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    An identity provider(IdP) is a services and manages digital identities.
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container className={classes.PanelRow} spacing={3}>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 8
                }}>
                <TextField
                  variant="outlined"
                  inputProps={{ maxLength: 250 }}
                  value={issuer}
                  onChange={(e) => setIssuer(e.target.value)}
                  fullWidth
                  label="Issuer"
                  helperText="OpenID provider."
                  required
                  disabled={readOnly}
                />
              </Grid>
            </Grid>
            <Grid container className={classes.PanelRow} spacing={3}>
              <Grid
                size={{
                  xs: 12,
                  sm: 12,
                  md: 8
                }}>
                <TextField
                  variant="outlined"
                  inputProps={{ maxLength: 250 }}
                  value={authorizationEndpoint}
                  onChange={(e) => setAuthorizationEndpoint(e.target.value)}
                  fullWidth
                  label="Authorization endpoint"
                  helperText="OpenID provider server endpoint."
                  required
                  disabled={readOnly}
                />
              </Grid>
            </Grid>
            <Grid container className={classes.PanelRow} spacing={3}>
              <Grid
                size={{
                  xs: 12,
                  sm: 12,
                  md: 8
                }}>
                <TextField
                  variant="outlined"
                  inputProps={{ maxLength: 250 }}
                  value={tokenEndpoint}
                  onChange={(e) => setTokenEndpoint(e.target.value)}
                  fullWidth
                  label="Token endpoint"
                  helperText="URL of your token endpoint."
                  required
                  disabled={readOnly}
                />
              </Grid>
            </Grid>
            <Grid container className={classes.PanelRow} spacing={3}>
              <Grid
                size={{
                  xs: 12,
                  sm: 12,
                  md: 8
                }}>
                <TextField
                  variant="outlined"
                  inputProps={{ maxLength: 250 }}
                  value={userInfoEndpoint}
                  onChange={(e) => setUserInfoEndpoint(e.target.value)}
                  fullWidth
                  label="User info endpoint"
                  helperText="URL of your user info endpoint."
                  required
                  disabled={readOnly}
                />
              </Grid>
            </Grid>
            <Grid container className={classes.PanelRow} spacing={3}>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 8
                }}>
                <TextField
                  variant="outlined"
                  inputProps={{ maxLength: 250 }}
                  value={clientID}
                  onChange={(e) => setClientID(e.target.value)}
                  fullWidth
                  label="Client ID"
                  helperText="The ID of an app client in your user pool."
                  required
                  disabled={readOnly}
                />
              </Grid>
            </Grid>
            <Grid container className={classes.PanelRow} spacing={3}>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 8
                }}>
                <TextField
                  variant="outlined"
                  inputProps={{ maxLength: 250 }}
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  fullWidth
                  label="Client Secret"
                  helperText="Provide a client secret associated with client ID."
                  type="password"
                  required
                  disabled={readOnly}
                />
              </Grid>
            </Grid>
          </PanelContainer>

          <PanelContainer title="Advanced Settings">
            <Grid container className={classes.PanelRow} spacing={3}>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4
                }}>
                <TextField
                  variant="outlined"
                  inputProps={{ maxLength: 50 }}
                  value={setupScript}
                  onChange={(e) => setSetupScript(e.target.value)}
                  fullWidth
                  label="Setup script"
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4
                }}>
                <FormControl variant="outlined" fullWidth required>
                  <InputLabel>Deployment Timeout</InputLabel>
                  <Select
                    value={deploymentTimeOut}
                    label="Deployment Timeout"
                    onChange={(e) => setDeploymentTimeOut(e.target.value)}
                    MenuProps={{ sx: { height: "220px" } }}
                  >
                    {deploymentTimeOutOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.text}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container className={classes.PanelRow} spacing={3}>
              <Grid
                size={{
                  xs: 12,
                  lg: 8
                }}>
                <TextField
                  variant="outlined"
                  value={envVariables}
                  inputProps={{ maxLength: 120 }}
                  onChange={(e) => setEnvVariables(e.target.value)}
                  fullWidth
                  label="Environment variables"
                  multiline
                  minRows={2}
                  maxRows={2}
                  helperText="List of key-value pairs in the PARAM1=Value1 PARAM2=Value2 format."
                />
              </Grid>
            </Grid>
          </PanelContainer>
        </Box>
      </Box>
    </CommonLayout>
  );
}
